import { FieldError, FieldErrors, Resolver } from 'react-hook-form'
import {
  AceCommodity,
  AceParty,
  AceShipment,
  AceTrailer,
  AceTripFormValues,
  toAceWeightUnit,
} from './ace-trip-form'

type ErrorMap = Record<string, unknown>

const requiredError = (message: string): FieldError => ({
  type: 'required',
  message,
})

const isBlank = (value?: string | null) => !String(value ?? '').trim()

const FAST_CARD_PATTERN = /^4270\d{8}(00|01|02)$/

const hasValue = (errors: object) => Object.keys(errors).length > 0

const requireText = (value: string | undefined, message: string) =>
  isBlank(value) ? requiredError(message) : undefined

const setIf = (errors: ErrorMap, key: string, error?: FieldError) => {
  if (error) errors[key] = error
}

const validateParty = (party: AceParty, label: string): ErrorMap => {
  const errors: ErrorMap = {}
  setIf(errors, 'name', requireText(party.name, `${label} Name is required`))
  setIf(
    errors,
    'address',
    requireText(party.address, `${label} Address is required`)
  )
  setIf(errors, 'city', requireText(party.city, `${label} City is required`))
  setIf(
    errors,
    'stateProvince',
    requireText(party.stateProvince, `${label} State / Province is required`)
  )
  setIf(
    errors,
    'postalCode',
    requireText(party.postalCode, `${label} Postal Code is required`)
  )
  return errors
}

const validateCommodity = (commodity?: AceCommodity): ErrorMap => {
  const errors: ErrorMap = {}
  const item = commodity ?? {
    description: '',
    quantity: '',
    packagingUnit: '',
    weight: '',
    weightUnit: '',
    loadedOn: '',
  }

  const description = item.description?.trim() ?? ''
  if (!description) {
    errors.description = requiredError('Description is required')
  } else if (description.length > 450) {
    errors.description = {
      type: 'validate',
      message: 'Description cannot be more than 450 characters',
    }
  }

  const quantity = item.quantity?.trim() ?? ''
  if (!quantity) {
    errors.quantity = requiredError('Quantity is required')
  } else if (!/^\d+$/.test(quantity) || Number(quantity) <= 0) {
    errors.quantity = {
      type: 'validate',
      message: 'Quantity must be a whole number greater than 0',
    }
  }

  const packaging = item.packagingUnit?.trim() ?? ''
  if (!packaging) {
    errors.packagingUnit = requiredError('Packaging Unit is required')
  } else if (!/^[A-Za-z]{3}$/.test(packaging)) {
    errors.packagingUnit = {
      type: 'validate',
      message: 'Packaging Unit must be 3 letters',
    }
  }

  const weight = item.weight?.trim() ?? ''
  if (!weight) {
    errors.weight = requiredError('Weight is required')
  } else if (!Number.isFinite(Number(weight)) || Number(weight) <= 0) {
    errors.weight = {
      type: 'validate',
      message: 'Weight must be greater than 0',
    }
  }

  const weightUnit = item.weightUnit?.trim() ?? ''
  if (!weightUnit) {
    errors.weightUnit = requiredError('Weight Unit is required')
  } else if (!toAceWeightUnit(weightUnit)) {
    errors.weightUnit = {
      type: 'validate',
      message: 'Enter a valid weight unit such as L or KG',
    }
  }

  return errors
}

const validateTrailer = (trailer: AceTrailer): ErrorMap => {
  const trailerPresent = [
    trailer.trailerNumber,
    trailer.trailerType,
    trailer.licensePlateNumber,
    trailer.licensePlateState,
  ].some((value) => !isBlank(value))
  if (!trailerPresent) return {}

  const errors: ErrorMap = {}
  setIf(
    errors,
    'trailerNumber',
    requireText(trailer.trailerNumber, 'Trailer Number is required')
  )
  setIf(
    errors,
    'trailerType',
    requireText(trailer.trailerType, 'Trailer Type is required')
  )
  setIf(
    errors,
    'licensePlateNumber',
    requireText(
      trailer.licensePlateNumber,
      'Trailer License Plate Number is required'
    )
  )
  setIf(
    errors,
    'licensePlateState',
    requireText(
      trailer.licensePlateState,
      'Trailer License Plate State / Province is required'
    )
  )
  return errors
}

const validateShipment = (shipment: AceShipment): ErrorMap => {
  const errors: ErrorMap = {}
  setIf(
    errors,
    'shipmentType',
    requireText(shipment.shipmentType, 'Shipment Type is required')
  )

  const controlNumber = shipment.shipmentControlNumber?.trim() ?? ''
  if (!controlNumber) {
    errors.shipmentControlNumber = requiredError(
      'Shipment Control Number is required'
    )
  } else if (controlNumber.length < 8 || controlNumber.length > 25) {
    errors.shipmentControlNumber = {
      type: 'validate',
      message: 'Shipment Control Number must be 8 to 25 characters',
    }
  }

  setIf(
    errors,
    'provinceOfLoading',
    requireText(
      shipment.provinceOfLoading,
      'State / Province of Loading is required'
    )
  )

  const shipperErrors = validateParty(shipment.shipper, 'Shipper')
  const consigneeErrors = validateParty(shipment.consignee, 'Consignee')
  if (hasValue(shipperErrors)) errors.shipper = shipperErrors
  if (hasValue(consigneeErrors)) errors.consignee = consigneeErrors

  const commodityErrors = validateCommodity(shipment.commodities?.[0])
  if (hasValue(commodityErrors)) {
    errors.commodities = [commodityErrors]
  }
  return errors
}

export const validateAceTripForm = (
  values: AceTripFormValues
): FieldErrors<AceTripFormValues> => {
  const errors: ErrorMap = {}

  setIf(errors, 'tripNumber', requireText(values.tripNumber, 'Trip Number is required'))
  setIf(
    errors,
    'usPortOfArrival',
    requireText(
      values.usPortOfArrival,
      values.manifestKind === 'ACI'
        ? 'Canadian Port of Arrival is required'
        : 'US Port of Arrival is required'
    )
  )
  setIf(
    errors,
    'estimatedArrival',
    requireText(
      values.estimatedArrival,
      'Estimated Arrival Date & Time is required'
    )
  )
  setIf(
    errors,
    'truckNumber',
    requireText(values.truckNumber, 'Truck Number is required')
  )
  setIf(
    errors,
    'truckLicensePlateNumber',
    requireText(
      values.truckLicensePlateNumber,
      'Truck License Plate Number is required'
    )
  )
  setIf(
    errors,
    'truckLicensePlateState',
    requireText(
      values.truckLicensePlateState,
      'Truck License Plate State / Province is required'
    )
  )
  setIf(
    errors,
    'driverFirstName',
    requireText(values.driverFirstName, 'Driver First Name is required')
  )
  setIf(
    errors,
    'driverLastName',
    requireText(values.driverLastName, 'Driver Last Name is required')
  )

  const fastCard = values.fastCardNumber?.trim() ?? ''
  if (fastCard && !FAST_CARD_PATTERN.test(fastCard)) {
    errors.fastCardNumber = {
      type: 'validate',
      message: 'FAST Card Number must be 14 digits in 4270…00/01/02 format',
    }
  }

  const trailerErrors = validateTrailer(values.trailer)
  if (hasValue(trailerErrors)) errors.trailer = trailerErrors

  const shipmentErrors = (values.shipments ?? []).map(validateShipment)
  if (shipmentErrors.some(hasValue)) {
    errors.shipments = shipmentErrors
  }

  return errors as FieldErrors<AceTripFormValues>
}

export const firstInvalidManifestTab = (
  errors: FieldErrors<AceTripFormValues>
) => {
  const detailsKeys: Array<keyof AceTripFormValues> = [
    'tripNumber',
    'usPortOfArrival',
    'estimatedArrival',
    'truckNumber',
    'truckLicensePlateNumber',
    'truckLicensePlateState',
    'trailer',
    'driverFirstName',
    'driverLastName',
    'fastCardNumber',
  ]
  if (detailsKeys.some((key) => Boolean(errors[key]))) return 0
  return errors.shipments ? 1 : 0
}

export const aceTripFormResolver: Resolver<AceTripFormValues> = (values) => {
  const errors = validateAceTripForm(values)
  return {
    values: hasValue(errors) ? ({} as AceTripFormValues) : values,
    errors,
  }
}
